import type { ConferenceContextState } from 'contexts/conference'
import { BreakoutRoom } from 'api/classroom/ClassroomState'
import { WseUserId } from 'api/types'
import { logBreakoutEvent } from 'services/breakoutFaroLogger'

export type UsersRoomAssignment = { userId: WseUserId; roomId: number; indexId: number }

export const getRoomIdForUser = (rooms: BreakoutRoom[], userId: WseUserId): number | null => {
    const room = rooms.find(r => r.users.includes(userId))
    return room !== undefined ? room.id : null
}

/**
 * Rebuilds each room's `users` from the modal's full assignment list (deduped per room).
 * Used when swapping so we don't chain multiple addRoomParticipant updates (which raced and duplicated users).
 */
export const applyUsersRoomsToRooms = (
    prevRooms: BreakoutRoom[],
    usersRooms: UsersRoomAssignment[]
): BreakoutRoom[] => {
    const byRoomId = new Map<number, WseUserId[]>()
    for (const r of prevRooms) {
        byRoomId.set(r.id, [])
    }
    for (const { userId, roomId } of usersRooms) {
        if (!byRoomId.has(roomId)) {
            byRoomId.set(roomId, [])
        }
        const list = byRoomId.get(roomId)!
        if (!list.includes(userId)) {
            list.push(userId)
        }
    }
    return prevRooms.map(room => ({
        ...room,
        users: byRoomId.get(room.id) ?? []
    }))
}

export const publishRoomDiffFromAssignments = (
    prevRooms: BreakoutRoom[],
    nextRooms: BreakoutRoom[],
    usersRooms: UsersRoomAssignment[],
    conference: ConferenceContextState,
    isSubsessionsEnabled: boolean
): void => {
    if (conference.status !== 'session-joined') return
    const { primus } = conference

    const indexByUser = new Map<WseUserId, number>()
    usersRooms.forEach(({ userId, indexId }) => {
        indexByUser.set(userId, indexId)
    })

    const allUserIds = new Set<WseUserId>()
    prevRooms.forEach(r => r.users.forEach(u => allUserIds.add(u)))
    nextRooms.forEach(r => r.users.forEach(u => allUserIds.add(u)))
    allUserIds.forEach(userId => {
        const oldRoom = getRoomIdForUser(prevRooms, userId)
        const newRoom = getRoomIdForUser(nextRooms, userId)
        if (isSubsessionsEnabled && oldRoom === newRoom) return

        logBreakoutEvent('room_assignment_changed', {
            userId,
            clientUserId: conference.participants.find(p => p.wseUserId === userId)?.clientUserId ?? 'unknown',
            fromRoom: oldRoom ?? -1,
            toRoom: newRoom ?? -1,
            source: 'local'
        })

        if (isSubsessionsEnabled && oldRoom !== null) {
            primus.send('breakout:remove-user', {
                user: userId,
                room: oldRoom,
                noCheck: false
            })
        }
        if (newRoom !== null) {
            primus.send('breakout:add-user', {
                user: userId,
                room: newRoom,
                index: indexByUser.get(userId),
                noCheck: false
            })
        }
    })
}
